import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkActionsModal = ({ 
  isOpen, 
  onClose, 
  selectedCategories, 
  categories, 
  onBulkAction 
}) => {
  const [action, setAction] = useState('');
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!action) return;

    setIsProcessing(true);
    
    try {
      await onBulkAction({
        action,
        categoryIds: selectedCategories,
        targetCategoryId: action === 'move' ? targetCategoryId : null,
        newStatus: action === 'status' ? newStatus : null
      });
      
      onClose();
      resetForm();
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setAction('');
    setTargetCategoryId('');
    setNewStatus('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const actionOptions = [
    { value: 'move', label: 'Move to Parent Category' },
    { value: 'status', label: 'Change Status' },
    { value: 'delete', label: 'Delete Categories' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const parentOptions = categories
    .filter(cat => !selectedCategories.includes(cat.id))
    .map(cat => ({
      value: cat.id,
      label: cat.name,
      description: `${cat.productCount} products`
    }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-elevation-3 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
              <Icon name="Settings" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bulk Actions</h2>
              <p className="text-sm text-muted-foreground">
                {selectedCategories.length} categories selected
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isProcessing}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <Select
            label="Select Action"
            options={actionOptions}
            value={action}
            onChange={setAction}
            placeholder="Choose an action"
            disabled={isProcessing}
          />

          {action === 'move' && (
            <Select
              label="Target Parent Category"
              options={[
                { value: '', label: 'Move to Root Level' },
                ...parentOptions
              ]}
              value={targetCategoryId}
              onChange={setTargetCategoryId}
              placeholder="Select parent category"
              disabled={isProcessing}
            />
          )}

          {action === 'status' && (
            <Select
              label="New Status"
              options={statusOptions}
              value={newStatus}
              onChange={setNewStatus}
              placeholder="Select status"
              disabled={isProcessing}
            />
          )}

          {action === 'delete' && (
            <div className="bg-error/10 border border-error/20 rounded-md p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-error mb-1">Warning: Permanent Action</h4>
                  <p className="text-sm text-error/80">
                    This will permanently delete the selected categories. Categories with products will be reassigned to their parent categories.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          
          <Button
            variant={action === 'delete' ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={!action || isProcessing || (action === 'move' && targetCategoryId === '') || (action === 'status' && !newStatus)}
            loading={isProcessing}
            iconName={action === 'delete' ? 'Trash2' : 'Check'}
            iconPosition="left"
          >
            {action === 'delete' ? 'Delete Categories' : 'Apply Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsModal;